import type { Pillar } from './pillars';

/**
 * Foundations — a free, five-day protocol that introduces all five pillars.
 *
 * Each day holds five micro-sections (~15 minutes with reading and reflection),
 * one per pillar. Language is PRT-informed: tracking, not fixing. No toxic
 * positivity, no "just relax", no spiritual bypassing.
 */

export interface FoundationSection {
  id: string;
  pillar: Pillar;
  title: string;
  /** Short teaching, written in the body-tracking voice. */
  body: string;
  /** The concrete instruction for this micro-practice. */
  action: string;
  /** Optional guided timer length in seconds. */
  timerSec?: number;
}

export interface FoundationDay {
  day: number;
  title: string;
  theme: string;
  emoji: string;
  /** Journal prompt shown on the day-completion screen. */
  journalPrompt: string;
  /** Protocol id teased once the day is complete. */
  teaserProtocolId: string;
  sections: FoundationSection[];
}

export const FOUNDATION_DAYS: FoundationDay[] = [
  {
    day: 1,
    title: 'Arrive',
    theme: 'Come back into the body and the room.',
    emoji: '🌅',
    journalPrompt: 'What did you notice about your body in the moments you stopped?',
    teaserProtocolId: 'somatic-foundations',
    sections: [
      {
        id: 'd1-s1',
        pillar: 'somatic',
        title: 'Arriving Breath',
        body: 'Before anything else, let your body know you have arrived. Feel the surface holding you — chair, floor, ground. You do not have to fix anything right now. You are only tracking what is here.',
        action: 'Breathe in for four, out for six. Notice the weight of your body.',
        timerSec: 90,
      },
      {
        id: 'd1-s2',
        pillar: 'movement',
        title: 'Shake',
        body: 'Shaking is one of the oldest ways a body discharges what it has been holding. It is not exercise, and it does not need to look good.',
        action: 'Stand and shake out your hands, arms, and legs. Let it be loose, not perfect.',
        timerSec: 35,
      },
      {
        id: 'd1-s3',
        pillar: 'digital',
        title: 'Turn Off Notifications',
        body: 'Notifications are interruptions dressed up as information. Each one asks your nervous system to orient toward the screen.',
        action: 'Open your phone settings and turn off notifications for one app that does not need them. Just one.',
      },
      {
        id: 'd1-s4',
        pillar: 'nature',
        title: 'Window Gaze',
        body: 'You do not need to go anywhere. Looking out a window is enough to let your eyes widen and your system soften.',
        action: 'Look out the nearest window. Let your gaze rest on something far away.',
        timerSec: 30,
      },
      {
        id: 'd1-s5',
        pillar: 'rest',
        title: '60 Seconds of Nothing',
        body: 'Doing nothing is a skill the body remembers. This is deliberate rest, not sleep, and not laziness.',
        action: 'Set the timer and do nothing at all. No phone, no plan. Just sit.',
        timerSec: 60,
      },
    ],
  },
  {
    day: 2,
    title: 'Notice',
    theme: 'Watch your body and your attention without judging either.',
    emoji: '👁️',
    journalPrompt: 'What did you learn from watching your own attention today?',
    teaserProtocolId: 'movement-grounding',
    sections: [
      {
        id: 'd2-s1',
        pillar: 'somatic',
        title: 'Body Scan',
        body: 'Scanning is not fixing. You are only tracking sensation — tight, warm, heavy, quiet. Every sensation belongs, and nothing needs to change.',
        action: 'Move your attention slowly from head to feet. Name what you notice without judging it.',
        timerSec: 90,
      },
      {
        id: 'd2-s2',
        pillar: 'digital',
        title: 'Check Your Notification Log',
        body: 'Most of us are interrupted far more than we realise. Seeing the number is the first step — not the fix, just the noticing.',
        action: 'Open your notification history or screen-time report. Just look. Do not change anything yet.',
      },
      {
        id: 'd2-s3',
        pillar: 'movement',
        title: 'Neck & Shoulders',
        body: 'The neck and shoulders are where many of us store "on alert". Moving here quietly says: you can come down.',
        action: 'Roll your shoulders back and tip your head side to side. Stop before it hurts.',
        timerSec: 60,
      },
      {
        id: 'd2-s4',
        pillar: 'nature',
        title: 'Listen for Three Sounds',
        body: 'Listening outward is a quiet way to orient. It tells your body the wider world is still there, and still okay.',
        action: 'Close your eyes and find three distinct sounds — one near, one middle, one far.',
        timerSec: 60,
      },
      {
        id: 'd2-s5',
        pillar: 'rest',
        title: 'Intentional Pause',
        body: 'A pause is not wasted time. It is how the nervous system catches up with the day.',
        action: 'Stop everything for thirty seconds. Let your next breath be slower than the last.',
        timerSec: 30,
      },
    ],
  },
  {
    day: 3,
    title: 'Step Out',
    theme: 'Take the practice outside, where the body expects to be.',
    emoji: '🌤️',
    journalPrompt: 'What changed when you stepped outside — in your body, not your head?',
    teaserProtocolId: 'nature-reconnection',
    sections: [
      {
        id: 'd3-s1',
        pillar: 'movement',
        title: 'Go Outside',
        body: 'Your body was built for open space. Stepping out is the whole practice — there is no gear and nothing to achieve.',
        action: 'Go outside; a doorstep counts. Stand there and let your eyes wander.',
        timerSec: 60,
      },
      {
        id: 'd3-s2',
        pillar: 'movement',
        title: 'Slow Walk',
        body: 'This is not a walk to anywhere. It is a walk to feel your feet meet the ground.',
        action: 'Walk slowly. Let your steps be unhurried and let your arms swing.',
        timerSec: 120,
      },
      {
        id: 'd3-s3',
        pillar: 'somatic',
        title: 'Orienting Outdoors',
        body: 'Orienting is turning toward what is around you and finding it safe enough to look around. Outdoors makes this easy.',
        action: 'Slowly turn your head left and right. Let your eyes land on three things you can see.',
        timerSec: 45,
      },
      {
        id: 'd3-s4',
        pillar: 'digital',
        title: 'No Phone This Session',
        body: 'Leaving the phone behind — even for ten minutes — changes what your attention is for.',
        action: 'Set your phone down inside, out of reach. Notice the small pull to check it, and let it pass.',
      },
      {
        id: 'd3-s5',
        pillar: 'nature',
        title: 'Cloud Gazing',
        body: 'Watching clouds is watching change without effort. Nothing you see is permanent — including how you feel.',
        action: 'Lie or stand and watch the sky. Follow one cloud until it shifts.',
        timerSec: 90,
      },
    ],
  },
  {
    day: 4,
    title: 'Unhook',
    theme: 'Loosen the grip, in the body and on the feed.',
    emoji: '🪝',
    journalPrompt: 'What did it feel like to unhook, even a little? What surprised you?',
    teaserProtocolId: 'digital-sovereignty',
    sections: [
      {
        id: 'd4-s1',
        pillar: 'movement',
        title: 'Full Body Shake',
        body: 'Shaking moves stuck energy without needing a story about it. You are not fixing — just discharging.',
        action: 'Shake your whole body — arms, legs, shoulders. Then stand still and notice.',
        timerSec: 30,
      },
      {
        id: 'd4-s2',
        pillar: 'somatic',
        title: 'Pendulation',
        body: 'Pendulation means moving gently between something difficult and something okay. You are safe enough to look around, and small doses are enough.',
        action: 'Rest attention on a neutral place — hands or breath. Touch the edge of a hard feeling for one breath, then return. Repeat.',
        timerSec: 120,
      },
      {
        id: 'd4-s3',
        pillar: 'digital',
        title: 'Unfollow One Account',
        body: 'Your feed is a room you choose to stand in. You are allowed to leave a room that leaves you activated.',
        action: 'Unfollow, mute, or remove one account that consistently drains or agitates you.',
      },
      {
        id: 'd4-s4',
        pillar: 'nature',
        title: 'Earthing',
        body: 'Skin on earth is an old, simple signal. No gear, no ritual — just contact.',
        action: 'If you can, take off your shoes and stand on grass, sand, or soil. Feel the contact.',
        timerSec: 60,
      },
      {
        id: 'd4-s5',
        pillar: 'rest',
        title: 'Extended Stillness',
        body: 'Stillness is not laziness. It is how the body finishes what movement started.',
        action: 'Sit or lie still. Let your body be heavy and your jaw loose.',
        timerSec: 120,
      },
    ],
  },
  {
    day: 5,
    title: 'Integrate',
    theme: 'Let the five days settle into something you keep.',
    emoji: '🌀',
    journalPrompt: 'What will you carry forward from these five days?',
    teaserProtocolId: 'all-protocols',
    sections: [
      {
        id: 'd5-s1',
        pillar: 'rest',
        title: 'NSDR',
        body: 'Non-sleep deep rest is deliberate rest while awake — lying down, letting the body scan itself, intending only to rest. It is not sleep, and you are not trying to fall asleep.',
        action: 'Lie down. Let attention drift slowly through your body. When it wanders, bring it back gently.',
        timerSec: 300,
      },
      {
        id: 'd5-s2',
        pillar: 'digital',
        title: 'Write 3 Tech Rules',
        body: 'Sovereignty starts as a few small rules you actually keep. Not a purge — a boundary.',
        action: 'Write three rules for how you will use your phone this week. Keep them small enough to keep.',
      },
      {
        id: 'd5-s3',
        pillar: 'nature',
        title: 'Find Your Outdoor Spot',
        body: 'A place you return to becomes a place your body recognises as safe.',
        action: 'Choose one outdoor spot near you. Go there, stand for a moment, and remember it.',
      },
      {
        id: 'd5-s4',
        pillar: 'movement',
        title: 'Repeat What Worked',
        body: 'Integration is repetition, not novelty. Do the one movement that helped most this week.',
        action: 'Repeat the movement from Days 1–4 that your body liked best.',
        timerSec: 60,
      },
      {
        id: 'd5-s5',
        pillar: 'somatic',
        title: 'Full Circle',
        body: 'Three breaths to close. You began by arriving; you finish by knowing you can return here any time. This belongs. This is allowed.',
        action: 'Take three slow breaths. On each exhale, let your shoulders drop.',
        timerSec: 60,
      },
    ],
  },
];

export const TOTAL_FOUNDATION_SECTIONS = FOUNDATION_DAYS.reduce(
  (sum, day) => sum + day.sections.length,
  0,
);

export function findFoundationDay(day: number): FoundationDay | undefined {
  return FOUNDATION_DAYS.find((entry) => entry.day === day);
}
